/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Distributor } from '@wsphere/distributors/domain';
import { Staff } from '@wsphere/staff/domain';
import { BehaviorSubject, combineLatest, map, shareReplay, switchMap } from 'rxjs';
import { RoleAssignmentsService, RoleBundlesService } from '../../services';
import { FullAdminWarningDialog } from '../full-administrator-warning-dialog';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-edit-team-member-dialog',
  templateUrl: './edit-team-member.dialog.html',
  standalone: false,
})
export class EditTeamMemberDialog {
  constructor(
    private fb: FormBuilder,
    private toastSvc: ToastService,
    private roleSvc: RoleAssignmentsService,
    private bundleSvc: RoleBundlesService
  ) {}

  @ViewChild(FullAdminWarningDialog) private warningDialog?: FullAdminWarningDialog;

  @PropertyListener('teamMember') staff$ = new BehaviorSubject<Staff.Model | undefined>(undefined);
  @Input() teamMember?: Staff.Model;

  @PropertyListener('permissionKey') permissionKey$ = new BehaviorSubject<string | null>(null);
  @Input() permissionKey: string | null = null;

  @Input() distributor?: Distributor.Model;

  @Output() memberUpdated = new EventEmitter();
  @ViewChild(DialogComponent) dialog!: DialogComponent;

  _submitting = false;
  resetFormValue = {
    memberRole: null,
  };

  // True from the first queued update until the refetch after the last one lands.
  saving = false;

  // Updates run one behind another - select-all fires several at once, so dropping them isn't an option.
  private queue: Promise<void> = Promise.resolve();
  private pendingOps = 0;

  // Latest in-flight fetches, stashed so a save can wait for the refetch to land before unlocking.
  private rolesFetch?: Promise<unknown>;
  private bundlesFetch?: Promise<unknown>;

  // Assigning a bundle creates role rows, so both lists share one refresh trigger.
  private refresh$ = new BehaviorSubject<null>(null);

  // Shared - three bindings read this, one request instead of three.
  roles$ = combineLatest([this.staff$, this.permissionKey$, this.refresh$]).pipe(
    switchMap(([staff, permissionKey]) => {
      this.rolesFetch = !staff
        ? Promise.resolve([])
        : this.roleSvc
            .getStaffRoles(staff.id)
            .then(roles => roles.filter(role => role.permissionKey === permissionKey));
      return this.rolesFetch as Promise<Staff.Security.RoleAssignment[]>;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Directly-assigned roles only; bundle-granted ones show on the bundle cards, so no role is listed twice.
  roleIds$ = this.roles$.pipe(
    map(roles => [...new Set(roles.filter(role => !role.bundleId).map(role => role.roleId))])
  );

  // What the member actually holds per bundle - not the bundle's current definition.
  readonly grantedRoleKeys$ = this.roles$.pipe(
    map(roles =>
      roles.reduce<Record<string, string[]>>((byBundle, role) => {
        if (!role.bundleId) return byBundle;

        byBundle[role.bundleId] = [...(byBundle[role.bundleId] ?? []), role.roleId];
        return byBundle;
      }, {})
    )
  );

  // Bundles held at the current scope (null = tenant-wide).
  readonly bundleIds$ = combineLatest([this.staff$, this.permissionKey$, this.refresh$]).pipe(
    switchMap(([staff, permissionKey]) => {
      this.bundlesFetch = !staff
        ? Promise.resolve([])
        : this.bundleSvc
            .listStaffBundles(staff.id, permissionKey ?? undefined)
            .then(bundles => bundles.map(bundle => bundle.id))
            .catch(() => []);
      return this.bundlesFetch as Promise<string[]>;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly form = this.fb.group({
    memberRole: [null as any, [Validators.required]],
  });

  private enqueue(op: () => Promise<void>): Promise<void> {
    this.pendingOps++;
    this.saving = true;
    this.queue = this.queue.then(op).then(async () => {
      if (--this.pendingOps) return;
      await this.settleRefresh();
      if (!this.pendingOps) this.saving = false;
    });
    return this.queue;
  }

  // Unlocking at request-completion would reopen the pickers on stale chips - wait for fresh data.
  private async settleRefresh() {
    this.refresh$.next(null);
    await Promise.allSettled([this.rolesFetch, this.bundlesFetch]);
  }

  addRole(roleId: string) {
    const teamMember = this.teamMember;
    if (!teamMember) throw new Error('No team member provided to dialog');
    return this.enqueue(async () => {
      try {
        await this.roleSvc.createRoleAssignment({
          roleId,
          staffId: teamMember.id,
          permissionKey: this.permissionKey || undefined,
        });
        this.toastSvc.show({
          type: 'success',
          title: 'Member successfully updated',
          text: `Added role ${Staff.Security.getRole(roleId)?.display.title} to ${teamMember.name}`,
        });

        if (roleId === '*') {
          this.warningDialog?.open(this.distributor?.name);
        }
      } catch (err) {
        console.error(err);
        this.toastSvc.show({
          type: 'error',
          title: 'Unexpected error',
          text: 'Something went wrong and the member was not updated. Please try again.',
        });
      }
    });
  }

  removeRole(roleId: string) {
    const teamMember = this.teamMember;
    if (!teamMember) throw new Error('No team member provided to dialog');
    return this.enqueue(async () => {
      try {
        await this.roleSvc.removeRoleAssignment({
          roleId,
          staffId: teamMember.id,
          ...(this.permissionKey && { permissionKey: this.permissionKey }),
        });
        this.toastSvc.show({
          type: 'success',
          title: 'Member successfully updated',
          text: `Removed role ${Staff.Security.getRole(roleId)?.display.title} from ${teamMember.name}`,
        });
      } catch (err) {
        // 404 = role already gone, resync is enough
        if ((err as { status?: number })?.status !== 404) {
          console.error(err);
          this.toastSvc.show({
            type: 'error',
            title: 'Unexpected error',
            text: 'Something went wrong and the member was not updated. Please try again.',
          });
        }
      }
    });
  }

  addBundle(bundleId: string) {
    const teamMember = this.teamMember;
    if (!teamMember) throw new Error('No team member provided to dialog');
    return this.enqueue(async () => {
      try {
        const { assigned } = await this.bundleSvc.assignToStaff({
          bundleId,
          staffId: teamMember.id,
          ...(this.permissionKey && { permissionKey: this.permissionKey }),
        });
        this.toastSvc.show({
          type: 'success',
          title: 'Bundle assigned',
          // Already-held roles are skipped, so `assigned` can be fewer.
          text: `Added ${assigned} role(s) to ${teamMember.name}.`,
        });
      } catch (err) {
        console.error(err);
        this.toastSvc.show({
          type: 'error',
          title: 'Unexpected error',
          text: 'Something went wrong and the bundle was not assigned. Please try again.',
        });
      }
    });
  }

  removeBundle(bundleId: string) {
    const teamMember = this.teamMember;
    if (!teamMember) throw new Error('No team member provided to dialog');
    return this.enqueue(async () => {
      try {
        await this.bundleSvc.removeFromStaff({
          bundleId,
          staffId: teamMember.id,
          ...(this.permissionKey && { permissionKey: this.permissionKey }),
        });
        this.toastSvc.show({
          type: 'success',
          title: 'Bundle removed',
          // Only this bundle's roles are dropped - manual ones stay.
          text: `Removed the bundle's roles from ${teamMember.name}.`,
        });
      } catch (err) {
        console.error(err);
        this.toastSvc.show({
          type: 'error',
          title: 'Unexpected error',
          text: 'Something went wrong and the bundle was not removed. Please try again.',
        });
      }
    });
  }

  inputIsValid() {
    return this.form.valid;
  }

  async resetForm() {
    try {
      this.form.reset(this.resetFormValue);
      this.form.markAsPristine();
    } catch (e) {
      console.error(e);
    }
  }

  closeDialog() {
    this.memberUpdated.emit();
    this.dialog.close();
  }

  openDialog() {
    this.dialog.open();
  }
}
